import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Droplets, PenTool, Plus, Trash2, Camera, Brain, FileText,
  ChevronDown, ChevronUp, Loader2, AlertTriangle, Check, X
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MoistureEquipmentTracker from "@/components/MoistureEquipmentTracker";
import FloorPlanSketch from "@/components/FloorPlanSketch";
import { AIResultNote } from "@/components/AIResultNote";

const ZONES = [
  { name: "Dry", min: 0, max: 15, color: "#22c55e", bg: "#052e16", risk: "None", action: "Monitor only" },
  { name: "Elevated", min: 16, max: 25, color: "#eab308", bg: "#422006", risk: "Low", action: "Targeted drying" },
  { name: "High", min: 26, max: 40, color: "#f97316", bg: "#431407", risk: "Moderate", action: "Active dehumidification" },
  { name: "Critical", min: 41, max: 70, color: "#ef4444", bg: "#450a0a", risk: "High", action: "Immediate extraction + drying" },
  { name: "Saturated", min: 71, max: 100, color: "#991b1b", bg: "#1c0505", risk: "Severe", action: "Material removal likely required" },
];

const MATERIALS = [
  "Drywall", "Hardwood Floor", "Concrete Slab", "Subfloor (OSB)",
  "Carpet/Pad", "Tile/Grout", "Insulation", "Structural Wood",
  "Ceiling Tile", "Plaster"
];

const CAUSES = [
  "Pipe Burst", "Roof Leak", "Appliance Leak", "Flooding/Storm",
  "Sewage Backup", "HVAC Condensation", "Window/Door Leak",
  "Foundation Seepage", "Unknown"
];

const ROOMS = [
  "Living Room", "Master Bedroom", "Bedroom", "Kitchen", "Bathroom",
  "Basement", "Garage", "Hallway", "Laundry Room", "Office",
  "Crawl Space", "Attic"
];

const PHOTO_TAGS = [
  "General", "Moisture Reading", "Affected Wall", "Floor Damage",
  "Equipment", "Before", "After"
];

function getZone(pct) {
  return ZONES.find(z => pct >= z.min && pct <= z.max) || ZONES[0];
}

export default function MoistureMapping({ requestId, jobInfo }) {
  const { aiOn } = useAI();
  const [activeTab, setActiveTab] = useState("info");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Map state
  const [room, setRoom] = useState(jobInfo?.room || "");
  const [cause, setCause] = useState(jobInfo?.cause || "");
  const [description, setDescription] = useState("");
  const [readings, setReadings] = useState([]);
  const [walls, setWalls] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [mapExists, setMapExists] = useState(false);

  // Canvas state
  const canvasRef = useRef(null);
  const [tool, setTool] = useState("view"); // view, wall, reading
  const [drawing, setDrawing] = useState(false);
  const [wallStart, setWallStart] = useState(null);
  const [mousePos, setMousePos] = useState(null);

  // Floor plan sketch
  const [floorPlanOpen, setFloorPlanOpen] = useState(false);
  const [floorPlanExists, setFloorPlanExists] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/floor-plans/${requestId}`);
        setFloorPlanExists(!!data.plan);
      } catch {}
    })();
  }, [requestId]);

  // Reading modal
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [readingPct, setReadingPct] = useState(30);
  const [readingMaterial, setReadingMaterial] = useState("Drywall");
  const [readingNotes, setReadingNotes] = useState("");

  // Photo upload
  const photoInputRef = useRef(null);

  // Load existing data
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const [mapRes, photoRes] = await Promise.all([
          API.get(`/moisture/job/${requestId}`),
          API.get(`/moisture/job/${requestId}/photos`).catch(() => ({ data: { photos: [] } })),
        ]);
        if (mapRes.data.exists) {
          setRoom(mapRes.data.room || "");
          setCause(mapRes.data.cause || "");
          setDescription(mapRes.data.initial_description || "");
          setReadings(mapRes.data.readings || []);
          setWalls(mapRes.data.walls || []);
          setAnalysis(mapRes.data.ai_analysis || null);
          setMapExists(true);
        }
        setPhotos(photoRes.data.photos || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [open, requestId]);

  // Save to backend
  const saveMap = useCallback(async (newReadings, newWalls) => {
    setSaving(true);
    try {
      await API.post(`/moisture/job/${requestId}`, {
        room, cause, initial_description: description,
        readings: (newReadings || readings).map(r => ({
          id: r.id, x: r.x, y: r.y, pct: r.pct,
          material: r.material, notes: r.notes, timestamp: r.timestamp,
        })),
        walls: newWalls || walls,
      });
      setMapExists(true);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  }, [requestId, room, cause, description, readings, walls]);

  // Canvas rendering
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    // Clear
    ctx.fillStyle = "#0a0f1a";
    ctx.fillRect(0, 0, w, h);

    // Grid
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 0.5;
    const gridSize = 30;
    for (let x = 0; x < w; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let y = 0; y < h; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }

    // Room border
    ctx.setLineDash([8, 4]);
    ctx.strokeStyle = "#334155";
    ctx.lineWidth = 2;
    ctx.strokeRect(15, 15, w - 30, h - 30);
    ctx.setLineDash([]);

    // Room label
    if (room) {
      ctx.font = "bold 12px monospace";
      ctx.fillStyle = "#64748b";
      ctx.fillText(room.toUpperCase(), 25, 35);
    }

    // Walls
    ctx.strokeStyle = "#94a3b8";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    walls.forEach(wall => {
      ctx.beginPath();
      ctx.moveTo(wall.x1 * w, wall.y1 * h);
      ctx.lineTo(wall.x2 * w, wall.y2 * h);
      ctx.stroke();
    });

    // Wall preview while drawing
    if (drawing && wallStart && mousePos) {
      ctx.setLineDash([6, 3]);
      ctx.strokeStyle = "#60a5fa";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(wallStart.x * w, wallStart.y * h);
      ctx.lineTo(mousePos.x * w, mousePos.y * h);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Reading points
    readings.forEach(r => {
      const px = r.x * w;
      const py = r.y * h;
      const zone = getZone(r.pct);
      const radius = 14 + (r.pct / 100) * 16;

      // Outer pulse
      ctx.beginPath();
      ctx.arc(px, py, radius + 4, 0, Math.PI * 2);
      ctx.fillStyle = zone.color + "20";
      ctx.fill();

      // Main circle
      ctx.beginPath();
      ctx.arc(px, py, radius, 0, Math.PI * 2);
      ctx.fillStyle = zone.color + "cc";
      ctx.fill();
      ctx.strokeStyle = zone.color;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Text
      ctx.fillStyle = "#fff";
      ctx.font = "bold 10px monospace";
      ctx.textAlign = "center";
      ctx.fillText(`${r.pct}%`, px, py - 2);
      ctx.font = "8px monospace";
      ctx.fillText(r.material?.split(" ")[0] || "", px, py + 9);

      // Point label below
      ctx.fillStyle = zone.color;
      ctx.font = "bold 9px monospace";
      ctx.fillText(r.pointLabel, px, py + radius + 14);
      ctx.textAlign = "start";
    });
  }, [readings, walls, room, drawing, wallStart, mousePos]);

  useEffect(() => { if (open && activeTab === "floorplan") drawCanvas(); }, [open, activeTab, drawCanvas]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };

  const handleCanvasDown = (e) => {
    e.preventDefault();
    const pos = getCanvasPos(e);
    if (!pos) return;
    if (tool === "reading") {
      setPendingPoint(pos);
      setReadingPct(30);
      setReadingMaterial("Drywall");
      setReadingNotes("");
      setShowReadingModal(true);
    } else if (tool === "wall") {
      setDrawing(true);
      setWallStart(pos);
    }
  };

  const handleCanvasMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    setMousePos(getCanvasPos(e));
  };

  const handleCanvasUp = (e) => {
    if (!drawing || !wallStart) return;
    const pos = getCanvasPos(e) || mousePos;
    if (pos) {
      const newWalls = [...walls, { x1: wallStart.x, y1: wallStart.y, x2: pos.x, y2: pos.y }];
      setWalls(newWalls);
      saveMap(null, newWalls);
    }
    setDrawing(false);
    setWallStart(null);
    setMousePos(null);
  };

  const saveReading = () => {
    if (!pendingPoint) return;
    const zone = getZone(readingPct);
    const newReading = {
      id: crypto.randomUUID(),
      pointLabel: `P${readings.length + 1}`,
      x: pendingPoint.x, y: pendingPoint.y,
      pct: readingPct, material: readingMaterial,
      zone: zone.name, notes: readingNotes,
      timestamp: new Date().toISOString(),
    };
    const newReadings = [...readings, newReading];
    setReadings(newReadings);
    setShowReadingModal(false);
    saveMap(newReadings, null);
    toast.success(`Reading ${newReading.pointLabel} saved: ${readingPct}% (${zone.name})`);
  };

  const deleteReading = async (id) => {
    const newReadings = readings.filter(r => r.id !== id).map((r, i) => ({ ...r, pointLabel: `P${i + 1}` }));
    setReadings(newReadings);
    try { await API.delete(`/moisture/job/${requestId}/reading/${id}`); } catch { }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tag", "General");
      formData.append("caption", "");
      try {
        const { data } = await API.post(`/moisture/job/${requestId}/photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 30000,
        });
        setPhotos(prev => [data, ...prev]);
        toast.success("Photo uploaded");
      } catch (err) {
        toast.error("Failed to upload photo");
      }
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await API.delete(`/moisture/job/${requestId}/photo/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch { toast.error("Failed"); }
  };

  const runAnalysis = async () => {
    if (readings.length === 0) { toast.error("Add readings first"); return; }
    setAnalyzing(true);
    try {
      const { data } = await API.post(`/moisture/job/${requestId}/analyze`);
      setAnalysis(data.analysis);
      setActiveTab("analysis");
      toast.success("AI Analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  // Stats
  const avgMoisture = readings.length ? Math.round(readings.reduce((s, r) => s + r.pct, 0) / readings.length) : 0;
  const maxMoisture = readings.length ? Math.max(...readings.map(r => r.pct)) : 0;
  const criticalCount = readings.filter(r => r.pct > 40).length;
  const maxZone = getZone(maxMoisture);

  const tabs = [
    { id: "info", label: "Job Info" },
    { id: "floorplan", label: "Floor Plan" },
    { id: "readings", label: `Readings (${readings.length})` },
    { id: "photos", label: `Photos (${photos.length})` },
    ...(aiOn ? [{ id: "analysis", label: "AI Analysis" }] : []),
  ];

  return (
    <div className="space-y-2" data-testid="moisture-mapping">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 rounded-lg bg-cyan-950 border border-cyan-800 hover:bg-cyan-900 transition-colors"
        data-testid="moisture-toggle">
        <div className="flex items-center gap-2">
          <Droplets className="w-4.5 h-4.5 text-cyan-400" />
          <span className="text-xs font-bold text-cyan-200 uppercase tracking-wider">
            Moisture Mapping System
          </span>
          {readings.length > 0 && (
            <Badge style={{ background: maxZone.bg, color: maxZone.color, borderColor: maxZone.color }}
              className="text-[9px] border">{maxMoisture}% MAX</Badge>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-cyan-400" /> : <ChevronDown className="w-4 h-4 text-cyan-400" />}
      </button>

      {/* Floor Plan Sketch entry — outside the toggle so it's always visible */}
      <Button onClick={() => setFloorPlanOpen(true)}
              className="w-full bg-zinc-900 border border-red-700/40 hover:bg-zinc-800 text-red-200 text-xs h-9"
              data-testid="floor-plan-entry">
        <PenTool className="w-3.5 h-3.5 mr-1.5" />
        {floorPlanExists ? "View / Edit Floor Plan Sketch" : "Create Floor Plan Sketch"}
      </Button>

      {floorPlanOpen && (
        <FloorPlanSketch requestId={requestId}
                         jobInfo={{ code: jobInfo?.code, ...jobInfo }}
                         onClose={() => { setFloorPlanOpen(false); setFloorPlanExists(true); }} />
      )}

      {open && (
        <div className="border border-cyan-900 rounded-lg overflow-hidden bg-[#020811]" data-testid="moisture-module">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Stats Bar */}
              {readings.length > 0 && (
                <div className="grid grid-cols-5 gap-px bg-[#0f172a] border-b border-[#1e293b]">
                  {[
                    { label: "READINGS", value: readings.length },
                    { label: "AVG %", value: `${avgMoisture}%`, color: getZone(avgMoisture).color },
                    { label: "MAX %", value: `${maxMoisture}%`, color: maxZone.color },
                    { label: "CRITICAL", value: criticalCount, color: criticalCount > 0 ? "#ef4444" : "#22c55e" },
                    { label: "PHOTOS", value: photos.length },
                  ].map((s, i) => (
                    <div key={i} className="p-2 text-center bg-[#020811]">
                      <p className="text-[8px] text-[#64748b] font-bold uppercase tracking-widest">{s.label}</p>
                      <p className="text-sm font-mono font-bold" style={{ color: s.color || "#e2e8f0" }}>{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b border-[#1e293b] overflow-x-auto">
                {tabs.map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === t.id ? "border-cyan-400 text-cyan-300 bg-[#0f172a]" : "border-transparent text-[#64748b] hover:text-[#94a3b8]"
                    }`} data-testid={`moisture-tab-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="p-3">
                {/* TAB 1: Job Info */}
                {activeTab === "info" && (
                  <div className="space-y-3" data-testid="moisture-info-tab">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-[#64748b] font-bold uppercase">Room / Area</label>
                        <select value={room} onChange={e => setRoom(e.target.value)}
                          className="w-full mt-1 px-2 py-2 bg-[#0f172a] border border-[#1e293b] rounded text-xs text-white"
                          data-testid="moisture-room-select">
                          <option value="">Select room</option>
                          {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#64748b] font-bold uppercase">Damage Cause</label>
                        <select value={cause} onChange={e => setCause(e.target.value)}
                          className="w-full mt-1 px-2 py-2 bg-[#0f172a] border border-[#1e293b] rounded text-xs text-white"
                          data-testid="moisture-cause-select">
                          <option value="">Select cause</option>
                          {CAUSES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748b] font-bold uppercase">Initial Description</label>
                      <Textarea value={description} onChange={e => setDescription(e.target.value)}
                        rows={3} placeholder="Describe the damage..." className="mt-1 bg-[#0f172a] border-[#1e293b] text-white text-xs"
                        data-testid="moisture-description" />
                    </div>
                    <Button onClick={() => { saveMap(); setActiveTab("floorplan"); }}
                      disabled={saving} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white text-xs"
                      data-testid="save-moisture-info">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Continue to Floor Plan"}
                    </Button>

                    {/* Zone Legend */}
                    <div className="pt-3 border-t border-[#1e293b]">
                      <p className="text-[10px] text-[#64748b] font-bold uppercase mb-2">Moisture Classification</p>
                      <div className="space-y-1">
                        {ZONES.map(z => (
                          <div key={z.name} className="flex items-center gap-2 p-2 rounded" style={{ background: z.bg, border: `1px solid ${z.color}30` }}>
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: z.color }} />
                            <span className="text-[10px] font-bold w-16" style={{ color: z.color }}>{z.name}</span>
                            <span className="text-[10px] text-[#94a3b8]">{z.min}-{z.max}%</span>
                            <span className="text-[10px] text-[#64748b] flex-1">{z.action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: Floor Plan */}
                {activeTab === "floorplan" && (
                  <div className="space-y-2" data-testid="moisture-floorplan-tab">
                    <div className="flex gap-1">
                      {[
                        { id: "reading", label: "Add Reading", icon: Plus },
                        { id: "wall", label: "Draw Wall", icon: PenTool },
                      ].map(t => (
                        <button key={t.id} onClick={() => setTool(t.id)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded text-[10px] font-bold uppercase ${
                            tool === t.id ? "bg-cyan-700 text-white" : "bg-[#0f172a] text-[#64748b] border border-[#1e293b]"
                          }`} data-testid={`tool-${t.id}`}>
                          <t.icon className="w-3 h-3" /> {t.label}
                        </button>
                      ))}
                      <button onClick={() => { setWalls([]); saveMap(null, []); }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded text-[10px] font-bold uppercase bg-[#0f172a] text-red-400 border border-[#1e293b] hover:bg-red-950"
                        data-testid="clear-walls">
                        <Trash2 className="w-3 h-3" /> Clear Walls
                      </button>
                    </div>

                    <canvas ref={canvasRef} width={700} height={437}
                      className="w-full rounded border border-[#1e293b] cursor-crosshair touch-none"
                      style={{ aspectRatio: "16/10" }}
                      onMouseDown={handleCanvasDown}
                      onMouseMove={handleCanvasMove}
                      onMouseUp={handleCanvasUp}
                      onTouchStart={handleCanvasDown}
                      onTouchMove={handleCanvasMove}
                      onTouchEnd={handleCanvasUp}
                      data-testid="moisture-canvas"
                    />

                    <p className="text-[10px] text-[#475569] text-center">
                      {tool === "reading" ? "Tap canvas to place a reading point" :
                       tool === "wall" ? "Click and drag to draw walls" : "Select a tool above"}
                    </p>
                  </div>
                )}

                {/* TAB 3: Readings Log */}
                {activeTab === "readings" && (
                  <div data-testid="moisture-readings-tab">
                    {readings.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="text-[#64748b] uppercase border-b border-[#1e293b]">
                              <th className="py-2 px-2 text-left">Point</th>
                              <th className="py-2 px-2 text-left">Material</th>
                              <th className="py-2 px-2 text-right">Moisture</th>
                              <th className="py-2 px-2 text-left">Zone</th>
                              <th className="py-2 px-2 text-left">Action</th>
                              <th className="py-2 px-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {readings.map(r => {
                              const zone = getZone(r.pct);
                              return (
                                <tr key={r.id} className="border-b border-[#0f172a] hover:bg-[#0f172a]">
                                  <td className="py-2 px-2 font-bold text-cyan-300 font-mono">{r.pointLabel}</td>
                                  <td className="py-2 px-2 text-[#e2e8f0]">{r.material}</td>
                                  <td className="py-2 px-2 text-right">
                                    <span className="font-mono font-bold text-sm" style={{ color: zone.color }}>{r.pct}%</span>
                                  </td>
                                  <td className="py-2 px-2">
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: zone.bg, color: zone.color, border: `1px solid ${zone.color}40` }}>
                                      {zone.name}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2 text-[#94a3b8]">{zone.action}</td>
                                  <td className="py-2 px-2">
                                    <button onClick={() => deleteReading(r.id)} className="text-red-500 hover:text-red-400">
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Droplets className="w-8 h-8 text-[#1e293b] mx-auto mb-2" />
                        <p className="text-xs text-[#475569]">No readings yet</p>
                        <p className="text-[10px] text-[#334155]">Go to Floor Plan tab to add readings</p>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 4: Photos */}
                {activeTab === "photos" && (
                  <div className="space-y-3" data-testid="moisture-photos-tab">
                    <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                    <button onClick={() => photoInputRef.current?.click()}
                      className="w-full p-6 border-2 border-dashed border-[#1e293b] rounded-lg text-center hover:border-cyan-700 transition-colors"
                      data-testid="moisture-photo-upload">
                      <Camera className="w-6 h-6 text-[#334155] mx-auto mb-1" />
                      <p className="text-xs text-[#475569]">Upload photos</p>
                    </button>

                    {photos.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {photos.map(p => (
                          <div key={p.id} className="bg-[#0f172a] rounded-lg overflow-hidden border border-[#1e293b]">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${p.file_url}`} alt={p.filename}
                              className="w-full h-24 object-cover" />
                            <div className="p-2 space-y-1">
                              <Badge className="text-[9px] bg-cyan-900 text-cyan-300 border border-cyan-700">{p.tag}</Badge>
                              <p className="text-[10px] text-[#94a3b8]">{p.caption || "No caption"}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-[8px] text-[#475569]">{new Date(p.timestamp).toLocaleTimeString()}</span>
                                <button onClick={() => deletePhoto(p.id)} className="text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 5: AI Analysis */}
                {aiOn && activeTab === "analysis" && (
                  <div className="space-y-3" data-testid="moisture-analysis-tab">
                    {analysis && <AIResultNote feature="moisture" refId={requestId} text={JSON.stringify(analysis)} />}
                    <Button onClick={runAnalysis} disabled={analyzing || readings.length === 0}
                      className="w-full bg-purple-700 hover:bg-purple-600 text-white text-xs h-10"
                      data-testid="run-analysis-btn">
                      {analyzing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing...</> : <><Brain className="w-4 h-4 mr-2" /> Run AI Analysis</>}
                    </Button>

                    {analysis && (
                      <div className="space-y-3" data-testid="analysis-results">
                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: "Severity", value: analysis.severity, color: analysis.severity === "Critical" ? "#ef4444" : analysis.severity === "Severe" ? "#f97316" : "#eab308" },
                            { label: "Water Class", value: `Class ${analysis.iicrcClass}` },
                            { label: "Drying Est.", value: analysis.dryingDays },
                            { label: "Mold Risk", value: analysis.moldRisk, color: analysis.moldRisk === "High" || analysis.moldRisk === "Critical" ? "#ef4444" : "#eab308" },
                          ].map((kpi, i) => (
                            <div key={i} className="p-3 bg-[#0f172a] rounded border border-[#1e293b]">
                              <p className="text-[8px] text-[#64748b] uppercase font-bold">{kpi.label}</p>
                              <p className="text-sm font-bold font-mono" style={{ color: kpi.color || "#e2e8f0" }}>{kpi.value}</p>
                            </div>
                          ))}
                        </div>

                        {/* Summary */}
                        <div className="p-3 bg-[#0f172a] rounded border border-[#1e293b]">
                          <p className="text-[10px] text-[#64748b] uppercase font-bold mb-1">Assessment Summary</p>
                          <p className="text-xs text-[#cbd5e1] leading-relaxed">{analysis.summary}</p>
                        </div>

                        {/* Affected Materials */}
                        {analysis.affectedMaterials?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-[#64748b] uppercase font-bold mb-1">Affected Materials</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-[10px]">
                                <thead>
                                  <tr className="text-[#64748b] border-b border-[#1e293b]">
                                    <th className="py-1.5 px-2 text-left">Material</th>
                                    <th className="py-1.5 px-2 text-right">Avg %</th>
                                    <th className="py-1.5 px-2">Status</th>
                                    <th className="py-1.5 px-2 text-left">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {analysis.affectedMaterials.map((m, i) => (
                                    <tr key={i} className="border-b border-[#0f172a]">
                                      <td className="py-1.5 px-2 text-[#e2e8f0]">{m.material}</td>
                                      <td className="py-1.5 px-2 text-right font-mono font-bold" style={{ color: getZone(m.avgMoisture).color }}>{m.avgMoisture}%</td>
                                      <td className="py-1.5 px-2 text-center">
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                          m.status === "Remove" ? "bg-red-950 text-red-400 border border-red-800" :
                                          m.status === "At Risk" ? "bg-orange-950 text-orange-400 border border-orange-800" :
                                          "bg-green-950 text-green-400 border border-green-800"
                                        }`}>{m.status}</span>
                                      </td>
                                      <td className="py-1.5 px-2 text-[#94a3b8]">{m.action}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Immediate Actions */}
                        {analysis.immediateActions?.length > 0 && (
                          <div className="p-3 rounded border border-red-900 bg-red-950/30">
                            <p className="text-[10px] text-red-400 uppercase font-bold mb-2">Immediate Actions Required</p>
                            <ol className="space-y-1">
                              {analysis.immediateActions.map((a, i) => (
                                <li key={i} className="text-xs text-[#fca5a5] flex gap-2">
                                  <span className="text-red-500 font-bold">{i + 1}.</span> {a}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Equipment & Drying Goals */}
                        <div className="grid grid-cols-2 gap-2">
                          {analysis.equipmentRecommended?.length > 0 && (
                            <div className="p-3 bg-[#0f172a] rounded border border-[#1e293b]">
                              <p className="text-[10px] text-[#64748b] uppercase font-bold mb-1">Equipment</p>
                              <ul className="space-y-0.5">
                                {analysis.equipmentRecommended.map((e, i) => (
                                  <li key={i} className="text-[10px] text-[#94a3b8] flex items-start gap-1">
                                    <span className="text-cyan-500 mt-0.5">-</span> {e}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {analysis.dryingGoals && (
                            <div className="p-3 bg-[#0f172a] rounded border border-[#1e293b]">
                              <p className="text-[10px] text-[#64748b] uppercase font-bold mb-1">Drying Goals</p>
                              {Object.entries(analysis.dryingGoals).map(([mat, goal], i) => (
                                <div key={i} className="text-[10px] text-[#94a3b8] flex justify-between">
                                  <span>{mat}</span><span className="font-mono text-cyan-400">{goal}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Insurance Notes */}
                        {analysis.insuranceNotes && (
                          <div className="p-3 rounded border border-green-900 bg-green-950/30">
                            <p className="text-[10px] text-green-400 uppercase font-bold mb-1">Insurance Documentation</p>
                            <p className="text-xs text-[#86efac] leading-relaxed">{analysis.insuranceNotes}</p>
                          </div>
                        )}

                        {/* Equipment Tracking */}
                        <div className="p-3 rounded border border-cyan-900 bg-cyan-950/20">
                          <MoistureEquipmentTracker requestId={requestId} aiAnalysis={analysis} />
                        </div>
                      </div>
                    )}

                    {!analysis && readings.length === 0 && (
                      <div className="text-center py-6">
                        <Brain className="w-8 h-8 text-[#1e293b] mx-auto mb-2" />
                        <p className="text-xs text-[#475569]">Add readings first to enable AI analysis</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Reading Modal */}
      <Dialog open={showReadingModal} onOpenChange={setShowReadingModal}>
        <DialogContent className="max-w-sm bg-[#0f172a] border-[#1e293b] text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-300 font-mono text-sm uppercase">New Moisture Reading</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Moisture Slider */}
            <div className="text-center">
              <p className="text-4xl font-mono font-bold" style={{ color: getZone(readingPct).color }}>
                {readingPct}%
              </p>
              <input type="range" min="0" max="100" value={readingPct}
                onChange={e => setReadingPct(parseInt(e.target.value))}
                className="w-full mt-2 accent-cyan-500"
                data-testid="moisture-slider" />
              <div className="mt-2 p-2 rounded" style={{ background: getZone(readingPct).bg, border: `1px solid ${getZone(readingPct).color}40` }}>
                <span className="text-xs font-bold" style={{ color: getZone(readingPct).color }}>
                  {getZone(readingPct).name}
                </span>
                <span className="text-[10px] text-[#94a3b8] ml-2">{getZone(readingPct).action}</span>
              </div>
            </div>

            {/* Material */}
            <div>
              <label className="text-[10px] text-[#64748b] font-bold uppercase">Material</label>
              <select value={readingMaterial} onChange={e => setReadingMaterial(e.target.value)}
                className="w-full mt-1 px-2 py-2 bg-[#020811] border border-[#1e293b] rounded text-xs text-white"
                data-testid="reading-material-select">
                {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="text-[10px] text-[#64748b] font-bold uppercase">Notes</label>
              <Textarea value={readingNotes} onChange={e => setReadingNotes(e.target.value)}
                rows={2} placeholder="Visible staining, odor, delamination..."
                className="mt-1 bg-[#020811] border-[#1e293b] text-white text-xs" />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowReadingModal(false)}
                className="flex-1 text-xs border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]">Cancel</Button>
              <Button onClick={saveReading}
                className="flex-1 text-xs bg-cyan-700 hover:bg-cyan-600 text-white"
                data-testid="save-reading-btn">Save Reading</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
